import { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  rating: number;
  quote: string;
  course: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Venthan Gowtham S.",
    role: "QA Engineer",
    company: "TechCorp",
    image: "/images/171599712_cfb4.jpg",
    rating: 5,
    quote: "Great effort and research, Karthick, in bringing us niche details on AI evaluation and tools. You make complex topics simple with clear, impactful content. Thanks for your hard work — keep it up, and continue inspiring us on this AI journey",
    course: "Using Gen AI & AI Agent in Software Automation Testing"
  },
  {
    id: 2,
    name: "Suhrob M.",
    role: "Full Stack Engineer",
    company: "Microsoft",
    image: "/images/20634750_151d.jpg",
    rating: 5,
    quote: "This is breathtaking perfect course with all the details covered for anyone to get started from basics to advanced concepts of ML using HuggingFace & Unsloth.",
    course: "LLM Fine-Tuning for Beginners: HuggingFace & Unsloth"
  },
  {
    id: 3,
    name: "Suparna D.",
    role: "Software Engineer",
    company: "Stripe",
    image: "/images/20455160_4bbc_3.webp",
    rating: 5,
    quote: "Third course from this instructor — and the best one yet! This is my third AI course in the series by this instructor (after taking the DeepEval + RAGAS and LangChain + RAG ones), and once again, it delivers beyond expectations.",
    course: "LLM Fine-Tuning for Beginners: HuggingFace & Unsloth"
  },
  {
    id: 4,
    name: "Kristof S.",
    role: "Tech Lead",
    company: "Airbnb",
    image: "/images/500072_cc77_2.jpg",
    rating: 5,
    quote: "The course was comprehensive and engaging, with practical projects that solidified my learning. Karthik is knowledgeable and articulative. I highly recommend it for anyone interested in AI development.",
    course: "Build Agentic AI & Multi-Agent System for Software Testing"
  },
  {
    id: 5,
    name: "Ricardo T.",
    role: "QA Engineer",
    company: "Shopify",
    image: "/images/34253740_34d0_2.jpg",
    rating: 5,
    quote: "This course didn't just teach me AI Agent testing — it changed how I see QA. From manual scripts to intelligent automation using GenAI, I've made a leap forward in my career. The instructor's clarity, real demos, and use of cutting-edge tools like LangChain + Ollama are simply next level. If you're even thinking of learning AI for software testing… this is the course.",
    course: "AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS"
  },
  {
    id: 6,
    name: "Alex Park",
    role: "Senior Developer",
    company: "Spotify",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face",
    rating: 5,
    quote: "The depth of knowledge and practical examples are unmatched. I've taken many courses, but this one stands out for its quality and real-world applicability.",
    course: "TypeScript for Beginners"
  }
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-rotate testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-secondary-300'
        }`}
      />
    ));
  };

  return (
    <section className="py-20 bg-gradient-to-br from-green-50 via-white to-emerald-50 overflow-hidden relative">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-green-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-emerald-400/10 rounded-full blur-2xl"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-slide-up">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-700 text-sm font-medium mb-6 shadow-sm">
            <Star className="w-4 h-4 mr-2 text-yellow-400 fill-current" />
            Student Success Stories
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            What Our <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Students Say</span>
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Join thousands of professionals who have transformed their careers through our courses. 
            Here's what they have to say about their learning journey.
          </p>
        </div>

        {/* Main Testimonial Carousel */}
        <div className="relative mb-16 animate-slide-up" style={{ animationDelay: '200ms' }}>
          <div className="relative max-w-4xl mx-auto">
            {/* Background decorative elements */}
            <div className="absolute -top-8 -left-8 w-16 h-16 text-green-200">
              <Quote className="w-full h-full" />
            </div>
            <div className="absolute -bottom-8 -right-8 w-16 h-16 text-green-200 transform rotate-180">
              <Quote className="w-full h-full" />
            </div>

            {/* Testimonial Card */}
            <div className="bg-white rounded-3xl shadow-2xl border border-green-100/50 p-8 md:p-12 relative overflow-hidden hover:shadow-3xl hover:shadow-green-500/10 transition-all duration-500 group">
              {/* Gradient overlay */}
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
              
              <div className="text-center">
                {/* Quote */}
                <blockquote className="text-xl md:text-2xl text-secondary-700 leading-relaxed mb-8 font-medium group-hover:text-secondary-800 transition-colors duration-300">
                  "{testimonials[currentIndex].quote}"
                </blockquote>

                {/* Rating */}
                <div className="flex justify-center mb-6">
                  <div className="flex space-x-1">
                    {renderStars(testimonials[currentIndex].rating)}
                  </div>
                </div>

                {/* Author Info */}
                <div className="flex items-center justify-center space-x-4">
                  <img
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-16 h-16 rounded-full border-4 border-green-100 shadow-lg group-hover:border-green-200 transition-colors duration-300"
                  />
                  <div className="text-left">
                    <h4 className="text-lg font-bold text-secondary-900">
                      {testimonials[currentIndex].name}
                    </h4>
                    <p className="text-secondary-600">
                      {testimonials[currentIndex].role} at {testimonials[currentIndex].company}
                    </p>
                    <p className="text-sm text-green-600 font-medium group-hover:text-green-700 transition-colors duration-300">
                      {testimonials[currentIndex].course}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border border-secondary-200 flex items-center justify-center hover:bg-green-50 hover:border-green-300 transition-all duration-300 group hover:scale-105"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6 text-secondary-600 group-hover:text-green-600 transition-colors" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg border border-secondary-200 flex items-center justify-center hover:bg-primary-50 hover:border-primary-300 transition-all duration-300 group"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6 text-secondary-600 group-hover:text-green-600 transition-colors" />
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-green-600 scale-125 shadow-lg shadow-green-500/50'
                    : 'bg-secondary-300 hover:bg-green-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Grid of Mini Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-slide-up" style={{ animationDelay: '400ms' }}>
          {testimonials.slice(0, 3).map((testimonial) => (
            <div
              key={`mini-${testimonial.id}`}
              className="bg-white rounded-2xl p-6 shadow-lg border border-green-100/50 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 group cursor-pointer"
            >
              <div className="flex items-start space-x-3 mb-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full border-2 border-green-100 group-hover:border-green-200 transition-colors duration-300"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-secondary-900">{testimonial.name}</h4>
                  <p className="text-sm text-secondary-600">{testimonial.company}</p>
                </div>
                <div className="flex space-x-0.5">
                  {renderStars(testimonial.rating).slice(0, 5).map((star, i) => (
                    <div key={i} className="w-4 h-4">
                      {star}
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-secondary-700 text-sm leading-relaxed line-clamp-3 group-hover:text-secondary-900 transition-colors">
                "{testimonial.quote.substring(0, 120)}..."
              </p>
              <p className="text-xs text-green-600 font-medium mt-3 group-hover:text-green-700 transition-colors duration-300">
                {testimonial.course}
              </p>
            </div>
          ))}
        </div>

        {/* Trust Indicators - Modern Green Theme */}
        <div className="mt-16 text-center animate-slide-up relative" style={{ animationDelay: '600ms' }}>
          <div className="inline-flex items-center space-x-6 sm:space-x-8 bg-white rounded-3xl px-6 sm:px-8 py-6 shadow-xl border border-green-100/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-500 group">
            <div className="text-center group-hover:transform group-hover:scale-105 transition-transform duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 group-hover:text-green-700 transition-colors duration-300">4.9/5</div>
              <div className="text-xs sm:text-sm text-secondary-600 group-hover:text-secondary-700 transition-colors duration-300">Average Rating</div>
              <div className="flex justify-center mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
            
            <div className="w-px h-12 bg-gradient-to-b from-green-200 to-emerald-200"></div>
            
            <div className="text-center group-hover:transform group-hover:scale-105 transition-transform duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 group-hover:text-green-700 transition-colors duration-300">370,000+</div>
              <div className="text-xs sm:text-sm text-secondary-600 group-hover:text-secondary-700 transition-colors duration-300">Happy Students</div>
              <div className="w-8 h-1 bg-green-300 rounded-full mx-auto mt-1 group-hover:bg-green-400 transition-colors duration-300"></div>
            </div>
            
            <div className="w-px h-12 bg-gradient-to-b from-green-200 to-emerald-200"></div>
            
            <div className="text-center group-hover:transform group-hover:scale-105 transition-transform duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 group-hover:text-green-700 transition-colors duration-300">95%</div>
              <div className="text-xs sm:text-sm text-secondary-600 group-hover:text-secondary-700 transition-colors duration-300">Success Rate</div>
              <div className="w-8 h-1 bg-emerald-300 rounded-full mx-auto mt-1 group-hover:bg-emerald-400 transition-colors duration-300"></div>
            </div>
          </div>
          
          {/* Floating accent elements */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-400 rounded-full opacity-60 animate-pulse"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-emerald-400 rounded-full opacity-40 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;